import { Link } from "react-router-dom";
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Facebook,
  Instagram 
} from "lucide-react";

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const services = [
    { name: "Tax Services", href: "/services#tax-services" },
    { name: "Immigration & Notary", href: "/services#immigration-notary" },
    { name: "Recruitment & Staffing", href: "/services#recruitment-staffing" },
    { name: "Credit Repair", href: "/services#credit-repair" },
    { name: "Business Development", href: "/services#business-development" },
  ];

  const quickLinks = [
    { name: "About Us", href: "/#about" },
    { name: "Our Services", href: "/services" },
    { name: "Book Appointment", href: "/booking" },
    { name: "Contact Us", href: "/contact" },
  ];

  const legalLinks = [
    { name: "Privacy Policy", href: "/privacy" },
    { name: "Terms & Conditions", href: "/terms" },
    { name: "Service Disclaimer", href: "/disclaimer" },
  ];

  return (
    <footer data-testid="footer" className="bg-[#0F392B] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_52cd4ddc-ea3b-4da0-bd43-138fc32e1b53/artifacts/hzuq3mo3_EB64C7B1-04AB-4D15-AAC4-830A6D0A1DAC.png" 
                alt="Espady Multi-Service Co. Logo" 
                className="h-16 w-auto brightness-0 invert"
              />
            </div>
            <p className="font-body text-white/80 text-sm leading-relaxed mb-6">
              Professional, reliable, and personalized support for individuals, 
              families, and small businesses. Your trusted partner for all your 
              service needs.
            </p>
            <div className="flex gap-4">
              <a
                href="https://www.facebook.com/share/1HgiRQNvMB/?mibextid=wwXIfr"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="facebook-link"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4AF37] transition-colors duration-300"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="https://www.instagram.com/espadymultiserviceco"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-link"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4AF37] transition-colors duration-300"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-[#D4AF37]">
              Our Services
            </h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service.name}>
                  <Link
                    to={service.href}
                    className="font-body text-sm text-white/80 hover:text-[#D4AF37] transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-[#D4AF37]">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="font-body text-sm text-white/80 hover:text-[#D4AF37] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-[#D4AF37]">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-white/80">
                  1368-B S Military Trail, Suite R<br />
                  West Palm Beach, FL 33415
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <a
                  href="tel:786-991-8919"
                  className="font-body text-sm text-white/80 hover:text-[#D4AF37] transition-colors"
                >
                  786-991-8919
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <a
                  href="mailto:espadywisellc@gmail.com"
                  className="font-body text-sm text-white/80 hover:text-[#D4AF37] transition-colors"
                >
                  espadywisellc@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="font-body text-sm text-white/80">
                  Mon - Fri: 9:00 AM - 6:00 PM<br />
                  Sat: 10:00 AM - 4:00 PM
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="font-body text-sm text-white/60">
              © {currentYear} Espady Multi-Service Co. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              {legalLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="font-body text-sm text-white/60 hover:text-[#D4AF37] transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
